/**
 * The Taiji fist (太极拳) ultimate sample's effects (user-approved prototype, docs/art/liked/fx-samples-2026-10-11/;
 * original design from public-domain taiji ideas), shared by the sample page (tools/fxSamples/taiji.ts) and the game's
 * port (lane FX-PORT, render/fx/portTaiji.ts). Moved here unchanged but for the hands: a scene says where both hands are
 * at a time (`hands`), so the page reads its posed rig and the game the fighter it draws. Every effect goes through the
 * FX pen (render/fx/pixelRules.ts `PixelPen`) on a 12 fps stepped clock.
 */
import { add, diamond, crescent, hash, isoRing, lerp, line, px, R, type G, type V } from './kit';

export const FPS = 12;
export const IMPACT = 2.0;
export const STOP = 2 / FPS;
export const STEP_IN = 12;
const A = (): readonly string[] => R.azure!;
const WH = (): readonly string[] => R.white!;
export const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
export const easeOut = (u: number) => 1 - (1 - u) * (1 - u);
export const frameSince = (t: number, at: number) => Math.floor((t - at) * FPS + 1e-6);
const LV = [0, 0.08, 0.2, 1] as const;
const level = (g: G, l: number) => {
  g.globalAlpha = LV[Math.max(0, Math.min(3, Math.round(l)))]!;
};

/** Where the ult plays: its clock (s), the caster's and the foe's feet, the unit direction between, the chest height. */
export interface Scene {
  t: number;
  a: V;
  f: V;
  d: V;
  chest: number;
  /** Both hands (A then B) at time t. */
  hands(t: number): [V, V];
}

// ── Effects ──

/**
 * An iso taiji disc (2:1) centred on `c`, radius `r` cells, turned by `th`: the yang half in warm white at `fill`, the
 * yin half in deep azure one level fainter, the S line and the eyes solid, `ring` (0..1) of the rim drawn from the back.
 */
export function taijiDisc(g: G, c: V, r: number, th: number, fill: number, ring: number, sLine: boolean, eyes: boolean): void {
  if (r < 2) return;
  const cx = Math.round(c[0]);
  const cy = Math.round(c[1]);
  const ry = r * 0.5;
  const cs = Math.cos(-th);
  const sn = Math.sin(-th);
  const region = (x: number, y: number): number => {
    const u = (x - cx) / r;
    const v = (y - cy) / ry;
    if (u * u + v * v > 1) return -1;
    const p = u * cs - v * sn;
    const q = u * sn + v * cs;
    const top = p * p + (q + 0.5) * (q + 0.5);
    const bot = p * p + (q - 0.5) * (q - 0.5);
    if (eyes && top < 0.03) return 2;
    if (eyes && bot < 0.03) return 3;
    return top < 0.25 || (p < 0 && bot >= 0.25) ? 0 : 1;
  };
  for (let y = Math.floor(cy - ry) - 1; y <= cy + ry + 1; y++)
    for (let x = Math.floor(cx - r) - 1; x <= cx + r + 1; x++) {
      const k = region(x, y);
      if (k < 0) continue;
      const INK = R.ink!;
      if (k === 2 || k === 3) {
        // The eyes: an ink dot in the white half, a white dot in the ink half.
        if (fill <= 0) continue;
        level(g, 3);
        px(g, x, y, k === 2 ? INK[1]! : WH()[8]!);
        continue;
      }
      const edge = region(x + 1, y) === 1 - k || region(x, y + 1) === 1 - k;
      if (fill > 0) {
        // The halves: master ink black and warm white, solid once drawn in (dithered on the step before).
        level(g, fill);
        px(g, x, y, k === 0 ? WH()[7]! : INK[1]!);
      } else if (sLine && edge) {
        level(g, 3);
        px(g, x, y, WH()[8]!);
      }
    }
  if (fill > 0) {
    // The 1-px ink rim round the filled disc.
    level(g, fill);
    isoRing(g, cx, cy, r, R.ink![0]!, false);
  }
  if (ring > 0) {
    level(g, 3);
    const from = -Math.PI / 2 + th;
    isoRing(g, cx, cy, r + 1, R.gold![4]!, true, from, from + Math.PI * 2 * ring);
    isoRing(g, cx, cy, r + 1, R.gold![6]!, false, from, from + Math.PI * 2 * ring);
  }
  g.globalAlpha = 1;
}

/** Aura variant: ก the round yin-yang aura, ข the eight-trigram ring. */
export type AuraKind = 'A' | 'B';
export const AURA_R: Record<AuraKind, number> = { A: 30, B: 12 };

/**
 * The yin-yang disc under the fighter (follows the step in): drawn on in steps (rim, S line, halves, eyes), turning
 * slowly; on the strike it is flung wide and dithered out. ก: the big round aura (≈2.3 m across); ข: a small disc.
 */
export function gatherDisc(g: G, s: Scene, at: V, kind: AuraKind): void {
  const t = s.t;
  if (t < 0.3) return;
  const e = frameSince(t, IMPACT);
  const R0 = AURA_R[kind];
  if (e >= 0) {
    if (e > 3) return;
    taijiDisc(g, at, R0 + 4 + e * 5, (IMPACT - 0.3) * 0.9 + e * 0.4, 3 - e, 0, false, e < 2);
    return;
  }
  const ring = clamp01((t - 0.3) / 0.45);
  const sLine = t >= 0.75;
  const fill = t < 0.95 ? 0 : t < 1.05 ? 2 : 3;
  const eyes = t >= 1.05;
  const coil = clamp01((t - 1.5) / 0.35);
  taijiDisc(g, at, R0, (t - 0.3) * 0.9 + coil * 0.8, fill, ring, sLine, eyes);
}

/** The same disc stamped under the foe on the hit (the force sent on), turning the other way, then dithered out. */
export function stampDisc(g: G, s: Scene): void {
  const e = frameSince(s.t, IMPACT);
  if (e < 0 || s.t >= 3.4) return;
  const r = e === 0 ? 16 : e === 1 ? 21 : 24;
  const th = -(s.t - IMPACT) * 2.4;
  const fill = s.t < 2.6 ? 3 : s.t < 2.95 ? 2 : s.t < 3.15 ? 1 : 0;
  const ring = s.t < 3.15 ? 1 : 0;
  taijiDisc(g, s.f, r, th, fill, ring, false, s.t < 3.15);
}

/** Points of the qi comet circling the body at waist height; `front` picks the half nearer the camera. */
export function orbit(g: G, s: Scene, at: V, front: boolean): void {
  const t = s.t;
  if (t < 0.45 || t >= 1.55) return;
  const c: V = [at[0], at[1] - s.chest * 0.8];
  const phi = (t - 0.45) * Math.PI * 2 * 1.3;
  const n = 12;
  for (let i = n; i >= 0; i--) {
    const a = phi - i * 0.17;
    const isFront = Math.sin(a) > 0;
    if (isFront !== front) continue;
    const p: V = [c[0] + Math.cos(a) * 16, c[1] + Math.sin(a) * 5];
    if (i === 0) diamond(g, p[0], p[1], 2, A()[4]!, WH()[8]!);
    else {
      level(g, i < 4 ? 3 : i < 8 ? 2 : 1);
      px(g, p[0], p[1], i < 4 ? WH()[7]! : A()[4]!);
      if (i < 4) px(g, p[0], p[1] + 1, A()[3]!);
      g.globalAlpha = 1;
    }
  }
}

/** Trails following the real hands: the newest part 2 cells deep and solid, the tail thin and dithered away. */
export function palmTrails(g: G, s: Scene): void {
  const t = s.t;
  const on = (t >= 0.3 && t < 1.55) || (t >= 1.7 && t < IMPACT + 0.1);
  if (!on) return;
  const N = 8;
  const pts: [V, V][] = [];
  for (let i = 0; i <= N; i++) {
    const ti = t - i / (FPS * 2);
    if (ti < 0.25) break;
    pts.push(s.hands(ti));
  }
  for (const side of [1, 0] as const)
    for (let i = pts.length - 2; i >= 0; i--) {
      const p = pts[i]![side];
      const q = pts[i + 1]![side];
      const l = i < 3 ? 3 : i < 6 ? 2 : 1;
      level(g, l);
      if (i < 3) line(g, [p[0], p[1] + 1], [q[0], q[1] + 1], A()[3]!);
      line(g, p, q, i < 2 ? WH()[8]! : A()[5]!);
    }
  g.globalAlpha = 1;
}

/** Motes spiralling in from the disc's rim up to the palms on the coil, then the orb held there. */
export function inward(g: G, s: Scene): void {
  const t = s.t;
  const [ha, hb] = s.hands(t);
  const hc = lerp(ha, hb, 0.5);
  if (t >= 1.4 && t < 1.85) {
    const u = clamp01((t - 1.4) / 0.42);
    for (let i = 0; i < 8; i++) {
      const a0 = (i * Math.PI * 2) / 8;
      const r = 30 * Math.pow(1 - u, 1.2);
      const a = a0 + u * Math.PI * 2.5;
      const y = s.a[1] + (hc[1] - s.a[1]) * u;
      const p: V = [hc[0] + Math.cos(a) * r, y + Math.sin(a) * r * 0.5];
      if (i % 2 === 0) diamond(g, p[0], p[1], 1, A()[4]!, WH()[8]!);
      else px(g, p[0], p[1], WH()[7]!);
    }
  }
  if (t >= 1.6 && t < IMPACT) {
    const r = t < 1.7 ? 1 : t < 1.8 ? 2 : 3;
    const p = t >= 1.85 ? ha : hc;
    diamond(g, p[0] + s.d[0] * 2, p[1], r, A()[4]!, WH()[8]!);
    if (r === 3) {
      px(g, p[0] + s.d[0] * 2 - 1, p[1], A()[1]!);
      px(g, p[0] + s.d[0] * 2 + 1, p[1], WH()[8]!);
    }
  }
}

/** Three crescent palm waves running from the palm to the foe, the arc opening back toward the caster. */
export function waves(g: G, s: Scene): void {
  const t = s.t;
  if (t < 1.85 || t >= IMPACT + STOP) return;
  const [ha] = s.hands(Math.min(t, IMPACT));
  const S: V = [s.f[0], s.f[1] - s.chest];
  const dir = Math.atan2(s.d[1], s.d[0]);
  for (let i = 0; i < 3; i++) {
    const k = clamp01((t - 1.85 - i * 1 / FPS) / (2 / FPS));
    if (t < 1.85 + i / FPS) continue;
    const r = 6 + i * 2;
    const c = lerp(ha, S, k);
    const o = add(c, s.d, -r);
    level(g, k >= 1 ? 2 : 3);
    crescent(g, o[0], o[1], r, dir - 1.0, dir + 1.0, 3 - (i === 0 ? 0 : 1), WH()[8]!, A()[4]!, 1, false);
  }
  g.globalAlpha = 1;
}

/** The hit: a star (long level rays, short diagonals) over a diamond, two frames, the second smaller and dithered. */
export function star(g: G, s: Scene): void {
  const e = frameSince(s.t, IMPACT);
  if (e < 0 || e > 1) return;
  const S: V = [s.f[0] - s.d[0] * 3, s.f[1] - s.chest];
  const L = e === 0 ? 11 : 7;
  const D = e === 0 ? 6 : 4;
  level(g, e === 0 ? 3 : 2);
  line(g, [S[0] - L, S[1]], [S[0] + L, S[1]], WH()[8]!);
  line(g, [S[0], S[1] - L + 2], [S[0], S[1] + L - 2], WH()[8]!);
  for (const [dx, dy] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const)
    line(g, [S[0] + dx * 2, S[1] + dy * 2], [S[0] + dx * D, S[1] + dy * D], A()[5]!);
  diamond(g, S[0], S[1], e === 0 ? 3 : 2, A()[5]!, WH()[8]!);
  g.globalAlpha = 1;
}

/** Two iso rings bursting out from the foe's feet, thick while fresh, dithered as they widen. */
export function burst(g: G, s: Scene): void {
  for (let j = 0; j < 2; j++) {
    const e = frameSince(s.t, IMPACT + j / FPS);
    if (e < 0 || e > 7) continue;
    const rx = 10 + e * 8 + j * 2;
    level(g, e < 3 ? 3 : e < 5 ? 2 : 1);
    isoRing(g, s.f[0], s.f[1], rx, j === 0 ? WH()[7]! : A()[4]!, e < 3);
  }
  g.globalAlpha = 1;
}

/** Dust kicked out at the foe's feet, and motes rising off the stamp. */
export function after(g: G, s: Scene): void {
  const e = frameSince(s.t, IMPACT);
  if (e < 0) return;
  const B = R.brown!;
  if (e < 7)
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + hash(i, 3);
      const r = 8 + e * 2.5;
      const x = s.f[0] + Math.cos(a) * r;
      const y = s.f[1] + Math.sin(a) * r * 0.5;
      level(g, e < 3 ? 3 : e < 5 ? 2 : 1);
      g.fillStyle = e < 3 ? B[6]! : B[5]!;
      g.fillRect(Math.round(x), Math.round(y) - 1, e < 3 ? 3 : 2, e < 3 ? 2 : 1);
    }
  if (e >= 1 && e < 14)
    for (let i = 0; i < 7; i++) {
      const b = e - 1 - (i % 3);
      if (b < 0) continue;
      const x = s.f[0] + (hash(i, 9) - 0.5) * 36;
      const y = s.f[1] + (hash(i, 11) - 0.5) * 10 - b * 2;
      level(g, b < 4 ? 3 : b < 8 ? 2 : 1);
      px(g, x, y, i % 2 ? WH()[8]! : A()[5]!);
      if (b < 4) px(g, x, y + 1, A()[3]!);
    }
  g.globalAlpha = 1;
}

/**
 * Aura ก, the round yin-yang aura: round the big disc a thick bright rim of stepped gold rings with a dithered halo
 * outside it, and a soft dithered dome shell over the fighter (its outline, and a meridian turning with the disc);
 * built up on the wind-up, held, flung out on the strike. Back parts behind the figure, `front` parts over it.
 */
export function roundAura(g: G, s: Scene, at: V, front: boolean): void {
  const t = s.t;
  if (t < 0.3) return;
  const Gd = R.gold!;
  const r = AURA_R.A;
  const e = frameSince(t, IMPACT);
  const [a0, a1] = front ? [0, Math.PI] : [Math.PI, Math.PI * 2];
  const arc = (rx: number, css: string, thick: boolean, cy = at[1], sweep = 1) => {
    const hi = a0 + (a1 - a0) * sweep;
    if (hi > a0) isoRing(g, at[0], cy, rx, css, thick, a0, hi);
  };
  if (e >= 0) {
    if (e > 4) return;
    // The burst: the rim flung out in two rings, thinning and dithering away.
    level(g, e < 2 ? 3 : e < 4 ? 2 : 1);
    arc(r + 6 + e * 9, Gd[e < 2 ? 5 : 4]!, e < 3);
    if (e < 3) arc(r + 8 + e * 9, Gd[3]!, false);
    level(g, e < 2 ? 2 : 1);
    arc(r + 2 + e * 6, Gd[6]!, false);
    g.globalAlpha = 1;
    return;
  }
  const draw = clamp01((t - 0.3) / 0.45);
  // The rim: a 2-cell gold ring (the pen's outline round it), a light line on its top, a 1-cell ring outside.
  level(g, 3);
  arc(r + 2, Gd[4]!, true, at[1], draw);
  arc(r + 2, Gd[6]!, false, at[1] - 1, draw);
  if (t >= 0.75) arc(r + 4, Gd[5]!, false);
  // The halo: two dithered rings further out, the outer fainter; they breathe a cell in and out.
  if (t >= 0.85) {
    const pulse = Math.floor(t * 4) % 2;
    level(g, 2);
    arc(r + 6 + pulse, Gd[4]!, false);
    level(g, 1);
    arc(r + 9 + pulse, Gd[3]!, false);
  }
  // The dome: its outline (an upright half-ellipse) and one meridian turning with the disc, behind the figure.
  if (!front && t >= 0.85) {
    const hgt = s.chest * 2 + 10;
    const n = 64;
    const rot = (t - 0.3) * 0.9;
    for (let i = 0; i <= n; i++) {
      const u = Math.PI * (i / n);
      const x = at[0] + Math.cos(u) * (r + 2);
      const y = at[1] - Math.sin(u) * hgt;
      level(g, t < 1.0 ? 1 : 2);
      px(g, x, y, Gd[5]!);
      const mx = at[0] + Math.cos(u) * (r + 2) * Math.cos(rot);
      level(g, 1);
      px(g, mx, at[1] - Math.sin(u) * hgt - Math.sin(rot) * Math.cos(u) * 4, Gd[6]!);
    }
    // Two latitude rings on the shell.
    level(g, 1);
    for (const k of [0.45, 0.8]) isoRing(g, at[0], at[1] - hgt * k, (r + 2) * Math.sqrt(1 - k * k), Gd[5]!, false, Math.PI, Math.PI * 2);
  }
  g.globalAlpha = 1;
}

/**
 * Aura ข, the eight-trigram ring: the trigrams of the Earlier Heaven (Fu Xi, 先天) order stand round the fighter as
 * ink-black bars with a 1-cell gold rim, rising out of the ground one after another on the wind-up and turning slowly;
 * stepped gold arcs crackle along the ring between them; on the strike the bars fly outward and break up.
 * Fu Xi order clockwise from the top of the ring: ☰ 乾 ☴ 巽 ☵ 坎 ☶ 艮 ☷ 坤 ☳ 震 ☲ 離 ☱ 兌 (opposites complement).
 */
export const FU_XI: readonly (readonly [number, number, number])[] = [
  // Lines bottom to top, 1 yang (solid), 0 yin (broken).
  [1, 1, 1], // ☰ qian
  [0, 1, 1], // ☴ xun
  [0, 1, 0], // ☵ kan
  [0, 0, 1], // ☶ gen
  [0, 0, 0], // ☷ kun
  [1, 0, 0], // ☳ zhen
  [1, 0, 1], // ☲ li
  [1, 1, 0], // ☱ dui
];
export const RING_R = 44;
/** Trigram stacks float with their middle bar at about waist height. */
export const WAIST_LIFT = 9;
export function slabAngle(i: number, t: number): number {
  return -Math.PI / 2 + (i * Math.PI) / 4 + Math.max(0, t - 0.4) * 0.5;
}
export function baguaRing(g: G, s: Scene, at: V, front: boolean): void {
  const t = s.t;
  if (t < 0.4) return;
  const INK = R.ink!;
  const Gd = R.gold!;
  const e = frameSince(t, IMPACT);
  const tt = Math.min(t, IMPACT);
  for (let i = 0; i < 8; i++) {
    const a = slabAngle(i, tt);
    const isFront = Math.sin(a) > 0;
    if (isFront !== front) continue;
    const fly = e >= 0 ? e : -1;
    if (fly > 6) continue;
    const rr = RING_R + (fly >= 0 ? fly * 7 : 0);
    const base: V = [at[0] + Math.cos(a) * rr, at[1] + Math.sin(a) * rr * 0.5];
    const w = Math.round(5 + 3 * Math.abs(Math.sin(a)));
    // Rising: each trigram comes up out of the ground in three steps, 0.1 s apart round the ring.
    const born = 0.45 + i * 0.09;
    if (t < born) continue;
    const lift = fly >= 0 ? 6 - fly : Math.min(3, frameSince(t, born) + 1) * 3;
    const ground = Math.round(base[1]);
    for (let k = 0; k < 3; k++) {
      // Bar k (0 bottom) sits 5 cells above the one under it; ink 2 rows inside a 1-cell gold rim.
      const yTop = ground - lift - 4 - k * 6;
      if (yTop + 3 > ground) continue;
      const yang = FU_XI[i]![k] === 1;
      const x0 = Math.round(base[0] - w / 2);
      const parts: [number, number][] = yang
        ? [[x0, w]]
        : [
            [x0, Math.floor((w - 1) / 2)],
            [x0 + w - Math.floor((w - 1) / 2), Math.floor((w - 1) / 2)],
          ];
      // Shattering: the bars spread apart and drop, rim first, then gone in dither.
      const spread = fly >= 0 ? (k - 1) * fly : 0;
      level(g, fly < 0 ? 3 : fly < 3 ? 3 : fly < 5 ? 2 : 1);
      for (const [px0, pw] of parts) {
        const xx = px0 + Math.round(Math.cos(a) * spread);
        const yy = yTop + (fly > 0 ? fly * (k + 1) : 0);
        // Thin rim: ink bar 3 rows tall with a 1-px gold line on its top edge only (no box).
        g.fillStyle = INK[1]!;
        g.fillRect(xx, yy, pw, 3);
        g.fillStyle = Gd[t >= IMPACT ? 6 : 5]!;
        g.fillRect(xx, yy - 1, pw, 1);
      }
    }
  }
  // Crackle: stepped zigzags along the ring between neighbouring trigrams, a few at a time, changing every frame.
  if (t >= 1.2 && e < 0) {
    const fr = Math.floor(t * FPS);
    for (let i = 0; i < 8; i++) {
      if (hash(i, fr) > 0.45) continue;
      const a0 = slabAngle(i, t) + Math.PI / 16;
      const mid = a0 + Math.PI / 4;
      if (Math.sin(mid) > 0 !== front) continue;
      let prev: V | null = null;
      for (let j = 0; j <= 10; j++) {
        const a = a0 + ((Math.PI / 2) * j) / 10;
        const jit = j === 0 || j === 10 ? 0 : Math.round((hash(i * 7 + j, fr) - 0.5) * 6);
        const p: V = [at[0] + Math.cos(a) * RING_R, at[1] + Math.sin(a) * RING_R * 0.5 - WAIST_LIFT - 9 + jit];
        if (prev) {
          level(g, 3);
          line(g, prev, p, j % 2 ? Gd[6]! : WH()[8]!);
        }
        prev = p;
      }
    }
    // The ring's own ground line, dithered, under the bars.
    if (!front) {
      level(g, 1);
      isoRing(g, at[0], at[1], RING_R, Gd[4]!, false);
    }
  }
  g.globalAlpha = 1;
}

export function aura(g: G, s: Scene, at: V, front: boolean, kind: AuraKind): void {
  if (kind === 'A') roundAura(g, s, at, front);
  else baguaRing(g, s, at, front);
}
